import { createAction, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { IActitvitySafety, IActivityWorkderModel, IActivityPTW, SafetyHealthOfficerResponse, ISafetyHealthOfficer, Worker, ICoAuthorModel } from '../../../api/hep/hep-screen-model';
import hepScreenApi from "../../../api/hep/hep-screen-api";
import { ACTIVITY_STATUS, HEP_REQUEST_STATUS } from '../../../constants/hep.constant';

export interface IMainDataContext {
  requestId: string;
  statusId: number;
  statusName: string;
  workTitleStartDate: string;
  workTitleEndDate: string;
  preparer: string;
  preparerId: string;
  hepStatuses: HEP_REQUEST_STATUS;
  totalCompletedStatus?: number;
  totalOnGoingStatus?: number;
  totalPlannedStatus?: number;
  totalPTWActiveStatus?: number;
  totalPTWInactiveStatus?: number;
  totalPTWCancelledStatus?: number;
  coAuthors: ICoAuthorModel[];
}

export interface IContractorInformationContext {
  scopeOfWork: string;
  safetyHealthOfficer: ISafetyHealthOfficer[];
  mainContractorId: string;
  mainContractor: string;
  contractId: string;
  contract: string;
  workTitleId: string;
  workTitle: string;
  startDate: string;
  endDate: string;
  safetyHealthOfficerResponses?: SafetyHealthOfficerResponse[];
  coAuthors: ICoAuthorModel[];
}

export interface IWorker {
  id: string;
  workerId: string;
  workerName: string;
  position: string;
  mainContractorId: string;
  accessValidity: string;
  criteria: string;
  workApplyId: string;
}

export interface IActivityContext {
  activityId: string;
  activityName: string;
  receivingAuthority: string[];
  stageId: number;
  preparer: string;
  activityStatus: ACTIVITY_STATUS;
  workLeader: IWorker[];
  numOfWorkers: number;
  numOfPTW: number;
  numOfSafety: number;
  dataList: {
    activityWorkers: IActivityWorkderModel[];
    activityPTWs: IActivityPTW[];
    activitySafeties: IActitvitySafety[];
  };
  pageNumber: number;
  pageSize: number;
  totalPtws: number;
  totalWorkers: number;
  totalSafeties: number;
  createdOn: Date; 
  startDate?: Date;
  endDate?: Date;
  isReadOnly: boolean;
}
export interface IWorkerData {
  workerList: Worker[],
  oshCoordinatorData: Worker[],
  siteSafetySupervisorData: Worker[],
  safetySupervisorData: Worker[],
  safetyHealthOfficerData: Worker[],
  onBulkSearching: boolean,
  receivingAuthoritiesList: IReceivingAuthorities[]
}

export interface IReceivingAuthorities {
  id: string,
  name: string,
  email: string
}

export interface GlobalHepContext {
  mainData: IMainDataContext;
  contractorInformation: IContractorInformationContext;
  activityList: IActivityContext[];
  activityDetail: IActivityContext;
  workerData: IWorkerData;
}

const GLOBAL_CONTEXT_INITIAL_STATE = {
  mainData: {},
  contractorInformation: {},
  activityList: [] as IActivityContext[],
} as GlobalHepContext;


export const _storeMainHepData = createAction<IMainDataContext>('/hep/storeMainData');
export const _storeContractorInformation = createAction<IContractorInformationContext>('/hep/storeContractorInformation');
export const _storeActivityList = createAction<IActivityContext[]>('/hep/storeActivityList');
export const _storeActivityDetails = createAction<IActivityContext>('/hep/storeActivityDetails');
export const _getWorkerData = createAsyncThunk("hep/getHepRequestData", hepScreenApi.getWorkerData);
export const _workerDataOnBulkSearch = createAction<boolean>('/hep/onBulkSearchMode');

export const hepContextSharedSlice = createSlice({
  name: 'hepContextShared',
  initialState: GLOBAL_CONTEXT_INITIAL_STATE,
  reducers: {
    setMainContext: (state, action) => {
      state.mainData = action.payload;
    },
    setContractorContext: (state, action) => {
      state.contractorInformation = action.payload;
    },
    setActivityList: (state, action) => {
      state.activityList = action.payload;
    },
    setActivityDetails: (state, action) => {
      state.activityDetail = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(_storeMainHepData, (state, action) => {
        state.mainData = action.payload;
      })
      .addCase(_storeContractorInformation, (state, action) => {
        state.contractorInformation = action.payload;
      })
      .addCase(_storeActivityList, (state, action) => {
        state.activityList = action.payload;
      })
      .addCase(_storeActivityDetails, (state, action) => {
        state.activityDetail = action.payload;
      })
      .addCase(_getWorkerData.pending, (state) => {
        state.workerData = {} as IWorkerData;
      })
      .addCase(_getWorkerData.fulfilled, (state, action) => {
        state.workerData.workerList = action.payload.data.data;
        state.workerData.oshCoordinatorData = action.payload.data.oshCoordinatorData;
        state.workerData.safetySupervisorData = action.payload.data.safetySupervisorData;
        state.workerData.siteSafetySupervisorData = action.payload.data.siteSafetySupervisorData;
        state.workerData.safetyHealthOfficerData = action.payload.data.safetyHealthOfficerData;
        state.workerData.receivingAuthoritiesList = action.payload.data.receivingAuthoritiesData;
      })
      .addCase(_getWorkerData.rejected, (state) => {
        state.workerData = {} as IWorkerData;
      })
      .addCase(_workerDataOnBulkSearch, (state, action) => {
        state.workerData.onBulkSearching = action.payload;
      })
  },
});

export default hepContextSharedSlice.reducer;
